// Directo brand palette: Violeta #853DFF, Azul #2719E6, Gris intenso #232323, Azul hielo #CDF3FF, Gris neutro #F3F4F4

// Brand of the app: violet leads in B2C (customer, onboarding), blue in B2B (commerce, backoffice, prospects,
// tienda-nube). Each app sets NEXT_PUBLIC_BRAND_TONE at build time; unset means B2C
export type BrandTone = 'b2b' | 'b2c';
export const BRAND_TONE: BrandTone = process.env.NEXT_PUBLIC_BRAND_TONE === 'b2b' ? 'b2b' : 'b2c';
const IS_B2B = BRAND_TONE === 'b2b';

export const COLORS = {
  // [1] is the brand's light tint (icon chips, skeletons): lavender in B2C, a cool blue in B2B so it sits with the Azul
  background: ['#F3F4F4', IS_B2B ? '#DCE6FF' : '#E0CEFF', '#FFF6ED', '#F3F4F4'],
  primary: {
    purple: { main: '#853DFF', light: '#C29EFF', light2: '#E0CEFF', lavender: '#A46DFF' },
    blue: { main: '#2719E6', light: '#938CF2', ice: '#CDF3FF' },
    green: { main: '#03FF00', light: '#D0E72F', dark: '#029500' },
    yellow: { light: '#C46A0A66' },
  },
  // The app's lead color (fills, borders, focus) and the other brand color as its small accent
  brand: IS_B2B
    ? { main: '#2719E6', light: '#938CF2', dark: '#140C73', accent: '#853DFF' }
    : { main: '#853DFF', light: '#C29EFF', dark: '#4A2799', accent: '#2719E6' },
  text: {
    main: '#232323',
    light: '#535353',
    // Darkened from #A7A7A8 to reach 4.5:1 contrast on white, same as customer-mobile
    light2: '#6E6E70',
    light3: '#667085',
    light4: '#E1E0E1',
    placeholder: '#6E6E70',
    inverted: '#ffffff',
    label: '#344054',
    // Secondary text on brand-colored headers
    onBrandMuted: IS_B2B ? '#D9D6FB' : '#EADCFF',
    // Brand text, links and icons on surfaces (fills use brand.main)
    brand: IS_B2B ? '#2719E6' : '#853DFF',
  },
  state: {
    error: '#D70E00',
    disabled: '#999999',
    warning: '#C46A0A',
    // Tonal disabled look for filled controls
    disabledSurface: '#ECE7F4',
    disabledText: '#9C92AE',
  },
  // Semantic tokens mirrored from customer-mobile: name the role, not the shade
  surface: {
    // Cards, dialogs and headers
    raised: '#FFFFFF',
    // Icon chips and highlighted blocks on white
    brandTint: IS_B2B ? '#EDF2FF' : '#F0E7FF',
    // Disabled inputs
    muted: '#F2F2F5',
    // Borderless filled cards, as on the website: a soft fill instead of a white card with an outline.
    // Detail and form cards (neutral blue-grey)
    card: '#EBEDF5',
    // Link and action cards (light brand)
    cardBrand: IS_B2B ? '#E6ECFF' : '#F1EAFF',
  },
  border: {
    // Hairlines on white: list separators, light banners
    faint: '#EEEEEE',
    // Outlines of cards, options and secondary inputs
    subtle: '#E2E2E8',
    // OTP boxes and unchecked tracks
    default: '#D1D5DB',
    // Unchecked checkboxes
    strong: '#C7C7CE',
    // Input outlines, dividers under white headers and above footers
    brand: IS_B2B ? '#CFDBF7' : '#E0CEFF',
  },
  gradient: {
    success: ['#03FF00', '#F3F4F4'],
    error: ['#FF8E64', '#F3F4F4'],
    // Lead color into a slightly deeper shade of itself
    brandHeader: IS_B2B ? ['#2719E6', '#1F14B8'] : ['#853DFF', '#6B2FE0'],
    balance: ['#2719E6', '#140C73'],
  },
  // The logo keeps its original colors, independent of the brand palette
  logo: { text: '#0039A6', chevron: '#863DFF' },
  chip: {
    info: { background: '#CDF3FF', text: '#2719E6' },
    success: { background: '#E5F3DF', text: '#3C9A1F' },
    warning: { background: '#fff6ed', text: '#c46a0a' },
    error: { background: '#FBE5E2', text: '#C8261B' },
  },
};

const TONE_RGB: Record<BrandTone, string> = { b2c: '133, 61, 255', b2b: '39, 25, 230' };
const ICE_RGB = '205, 243, 255';

// Brand background gradients, as CSS backgrounds layered over the neutral background
export const BRAND_GRADIENTS = {
  // Brand color at the top fading out, with an ice-blue glow in the bottom-right corner (login pages)
  mesh: (tone: BrandTone) =>
    `radial-gradient(70% 60% at 100% 100%, rgb(${ICE_RGB}) 0%, rgba(${ICE_RGB}, 0) 100%), ` +
    `linear-gradient(180deg, rgb(${TONE_RGB[tone]}) 0%, rgba(${TONE_RGB[tone]}, 0.7) 25%, ` +
    `rgba(${TONE_RGB[tone]}, 0.3) 55%, rgba(${TONE_RGB[tone]}, 0) 85%), ${COLORS.background[0]}`,
  // Ice-blue top fading into the background, with a brand glow rising from the bottom (wait screens)
  glow: (tone: BrandTone) =>
    `radial-gradient(120% 40% at 50% 100%, rgba(${TONE_RGB[tone]}, 0.85) 0%, rgba(${TONE_RGB[tone]}, 0.35) 45%, ` +
    `rgba(${TONE_RGB[tone]}, 0) 100%), linear-gradient(180deg, rgb(${ICE_RGB}) 0%, ${COLORS.background[0]} 50%)`,
  // Faint ice-blue and brand tints in the top corners, gone before mid-page (app screens behind cards)
  wash: (tone: BrandTone) =>
    `radial-gradient(55% 60% at 100% 0%, rgba(${ICE_RGB}, 0.7) 0%, rgba(${ICE_RGB}, 0) 100%), ` +
    `radial-gradient(70% 55% at 0% 0%, rgba(${TONE_RGB[tone]}, 0.1) 0%, rgba(${TONE_RGB[tone]}, 0) 100%), ` +
    `${COLORS.background[3]}`,
  // Hero cards (order total, prospect header), as on the website: ice blue on the left into a light brand tint
  cardWash: () => `linear-gradient(105deg, #D9F3FC 0%, ${IS_B2B ? '#DCE4FF' : '#E6DAFD'} 100%)`,
};

// Solid stand-in for the wash's top-left corner (the brand tint at 10% over the background), for the browser's
// theme-color, which takes a single color
export const WASH_THEME_COLOR: Record<BrandTone, string> = { b2c: '#E8E2F5', b2b: '#DFDEF3' };
// The mesh's top edge (solid brand color), for the theme-color on login pages
export const MESH_THEME_COLOR: Record<BrandTone, string> = { b2c: '#853DFF', b2b: '#2719E6' };

// Spacing scale in px, same as customer-mobile
export const SPACING = {
  xxs: 4,
  xs: 8,
  sm: 12,
  md: 16,
  lg: 20,
  xl: 24,
  xxl: 32,
  xxxl: 40,
};

export const RADIUS = {
  sm: 6,
  md: 8,
  lg: 12,
  xl: 16,
  xxl: 20,
  full: 999,
};

export const SHADOWS = {
  // Soft purple-grey shadow that separates borderless cards from the lavender background
  card: '0px 4px 16px rgba(81, 71, 108, 0.08)',
  // Floating banners and toasts
  floating: '0px 4px 24px rgba(0, 0, 0, 0.15)',
};

/** Overrides MaterialIcon's table display and inherited line height so the glyph centers inside a flex box. */
export const CENTERED_ICON = {
  display: 'flex !important',
  alignItems: 'center',
  justifyContent: 'center',
  textAlign: 'center',
  lineHeight: '1 !important',
} as const;

export const MOTION = {
  pressScale: 0.97,
  cardPressScale: 0.98,
  fast: 150,
  short: 200,
  medium: 250,
  easeOut: 'cubic-bezier(0.33, 1, 0.68, 1)',
  easeIn: 'cubic-bezier(0.32, 0, 0.67, 0)',
  stagger: 60,
  maxStaggerIndex: 8,
};

/**
 * Styles that fade an element in while sliding it up, as customer-mobile screens do. Spread into an existing `sx`
 * to animate a page block or list item without wrapping it; works in server components too. Uses the theme's global
 * `directoFadeInUp` keyframes, and the theme's reduced-motion rule turns it off.
 *
 * @param {number} index - Position on the page or in a list; each step delays the entrance by 60ms, capped at 8.
 */
/**
 * Page panel on phones: a sheet rising from the background, rounded on top and square where it meets the screen
 * bottom, stretched to fill the column. From md up it's the usual rounded card. Pair it with 8px of top spacing on
 * phones so the background shows above the rounded top.
 */
export const MOBILE_SHEET_SX = {
  flex: { xs: 1, md: 'unset' },
  borderRadius: { xs: `${RADIUS.xl}px ${RADIUS.xl}px 0 0`, md: `${RADIUS.xl}px` },
} as const;

export const fadeInUpSx = (index = 0) => ({
  animation: `directoFadeInUp ${MOTION.medium}ms ${MOTION.easeOut} backwards`,
  animationDelay: `${Math.min(index, MOTION.maxStaggerIndex) * MOTION.stagger}ms`,
});

/** Hides content visually while keeping it available to assistive technologies. */
export const VISUALLY_HIDDEN = {
  border: 0,
  clip: 'rect(0 0 0 0)',
  height: '1px',
  margin: '-1px',
  overflow: 'hidden',
  padding: 0,
  position: 'absolute',
  whiteSpace: 'nowrap',
  width: '1px',
} as const;

/** Disables an sx transition (including `!important` ones) when the user prefers reduced motion. */
export const REDUCED_MOTION_NO_TRANSITION = {
  '@media (prefers-reduced-motion: reduce)': { transition: 'none !important' },
} as const;
